import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { useUpdateState } from '~/hooks/useUpdateState'
import { requestUpdateActivation } from '~/services/updateService'
import styles from './UpdateNotice.module.css'

/**
 * The update-available surface (D-024, D-044, docs/ui-ux-spec.md).
 *
 * Three visible states, and the distinction between the first two is the whole
 * point of D-044:
 *
 *   deferred          a badge only. It states that an update is ready and that
 *                     it will wait. No action, no reload, nothing to tap.
 *   promptable        an explicit action the user chooses.
 *   activationFailed  the draft save failed, so the update was NOT applied.
 *                     The message says so plainly and the action can be retried.
 *
 * Nothing here reloads on its own. `idle` and the two in-flight statuses render
 * nothing rather than a spinner that would compete with the user's work.
 */
export function UpdateNotice() {
  const state = useUpdateState()
  const [failure, setFailure] = useState<string | null>(null)

  const apply = async () => {
    const result = await requestUpdateActivation()
    setFailure(result.ok ? null : describeFailure(result.error))
  }

  if (state.status === 'deferred') {
    return (
      <aside class={styles.notice} role="status">
        <p class={styles.text}>
          An update is ready. It will wait until you finish your weekly review — your work is not
          interrupted.
        </p>
      </aside>
    )
  }

  /* `activating` is included so the notice does not vanish and reappear while
     the draft is being saved. It stays put and reports that it is working. */
  if (
    state.status === 'promptable' ||
    state.status === 'activating' ||
    state.status === 'activationFailed'
  ) {
    return (
      <aside class={styles.notice} role="status">
        <p class={styles.text}>
          {state.status === 'activationFailed'
            ? 'The update was not applied because your work could not be saved. Nothing was lost.'
            : 'An update is ready to install.'}
        </p>
        {failure === null ? null : <p class={styles.failure}>{failure}</p>}
        <Button onClick={apply} busy={state.status === 'activating'}>
          {state.status === 'activationFailed' ? 'Try again' : 'Update now'}
        </Button>
      </aside>
    )
  }

  return null
}

function describeFailure(error: { kind: string; reason?: string }): string {
  if (error.kind === 'draftSaveFailed') {
    return `Could not save your work: ${error.reason ?? 'unknown reason'}. The update was not applied.`
  }
  return 'The update cannot be applied right now. Finish or leave your weekly review first.'
}
